/**
 * Component displaying the information of the current plan
 * for the billing page.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright (c) Ircam Amplify 2025
 */

import {
  AccountUtil,
  SubscriptionFrequency,
  TextUtil,
  TierType,
  TierTypeHumanReadable,
  Util,
} from '@amplify3/duplo-back-end'
import { BiCoin } from 'react-icons/bi'
import { Link } from 'react-router-dom'
import Button from '../../components/Button'
import './billing.css'
import { VerticalDivider } from './Divider'
import { PlanInvoice } from './Invoice'
import './PlanOverview.css'
interface PlanInfoProps {
  className?: string
  invoice?: PlanInvoice
}

const PlanOverview = (props: PlanInfoProps) => {
  const fullClassName = `${props.className} plan-overview-container`

  /**
   * The name of the plan, depending on the tier.
   */
  const planName = (() => {
    if (!props.invoice || TierType.Unknown == props.invoice.tier) {
      return ''
    }

    const plan = `${TierTypeHumanReadable[props.invoice.tier]} Plan`
    if (TierType.Subscription == props.invoice.tier) {
      const subscriptionFreq = props.invoice.frequency
      return `${TextUtil.capitalizeFirstChar(subscriptionFreq)} ${plan}`
    } else {
      return plan
    }
  })()

  interface PriceValueProps {
    value: number | undefined
    currency: string | undefined
    perUnit: string | undefined
  }

  const PriceValue = (props: PriceValueProps) => {
    return (
      <div className="price-display">
        {undefined !== props.value && undefined !== props.currency && (
          <span className="price-value">
            {TextUtil.formatPrice(props.value, props.currency)}
          </span>
        )}{' '}
        {props.perUnit}
      </div>
    )
  }

  const PriceContainer = () => {
    return (
      <div className="plan-price-container">
        {TierType.PayAsYouGo == props.invoice?.tier && (
          <PriceValue
            value={props.invoice?.planPrice}
            currency={props.invoice?.currency}
            perUnit="credit used"
          />
        )}

        {TierType.Subscription == props.invoice?.tier && (
          <>
            <PriceValue
              value={props.invoice?.planPrice}
              currency={props.invoice?.currency}
              perUnit={props.invoice?.frequency}
            />

            <VerticalDivider height="10px" />

            <PriceValue
              value={props.invoice?.offPackageUnitCost}
              currency={props.invoice?.currency}
              perUnit="off-package credit"
            />
          </>
        )}
      </div>
    )
  }

  const PriceDescription = () => {
    const now = new Date()
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)

    const FormattedDate = ({ date }: { date: Date | undefined }) => {
      return (
        <span className="bold">
          {date ? TextUtil.dateToMonthDayWithOrdinal(date) : 'an unknown date'}
        </span>
      )
    }

    return (
      <div className="plan-description">
        {TierType.Free == props.invoice?.tier && (
          <>
            You are currently on the Free Plan. Upgrade to unlock unlimited
            consumption.
          </>
        )}

        {TierType.PayAsYouGo == props.invoice?.tier && (
          <>
            You will be billed on <FormattedDate date={lastDayOfMonth} /> for
            your past monthly credit consumption.
          </>
        )}

        {TierType.Subscription == props.invoice?.tier && (
          <>
            <>
              You received {props.invoice?.baseCreditMax} credits on{' '}
              <FormattedDate date={props.invoice?.periodStart} />.{' '}
            </>

            {SubscriptionFrequency.Yearly == props.invoice?.frequency ? (
              <>
                You have <span className="bold">one year</span> to consume them
                before automatic yearly refill.
              </>
            ) : (
              <>
                You have until <FormattedDate date={props.invoice?.periodEnd} />{' '}
                to use your credits before next automatic monthly refill.
              </>
            )}

            <>
              {' '}
              Additional credits consumed during this period will be charged on
              your next billing date.
            </>
          </>
        )}

        {TierType.TopUpCreditAimd == props.invoice?.tier && (
          <>
            You are using a Top-Up plan.
          </>
        )}

        {AccountUtil.isTierEnterprise(props.invoice?.tier) && (
          <>
            You have a custom contract, for any request please contact suport
            team.
          </>
        )}
      </div>
    )
  }

  interface ProgressBarProps {
    progress: number // 0 to 1
    className?: string
  }

  const ProgressBar = ({ progress, className = '' }: ProgressBarProps) => {
    const clampedProgress = Math.min(Math.max(progress, 0), 1)

    return (
      <div className="progress-bar-container">
        <div
          className={`progress-bar-fill ${className}`}
          style={{ width: `${clampedProgress * 100}%` }}
        />
      </div>
    )
  }

  interface CreditBarProps {
    className?: string
    label: string
    maxCount: number
    usedCount: number
    isMaxDisplayed?: boolean
  }

  /**
   * The component displaying a credit count with a progress bar.
   */
  const CreditBar = (props: CreditBarProps) => {
    const className = props.className ?? ''
    const isMaxDisplayed = props.isMaxDisplayed ?? false

    const countText = isMaxDisplayed
      ? `${Util.ceilTo2Digits(props.usedCount)}/${props.maxCount}`
      : `${Util.ceilTo2Digits(props.usedCount)}`
    const percent = props.usedCount / props.maxCount
    const creditText = props.usedCount > 0 ? 'credits' : 'credit'

    return (
      <div className={`credit-bar`}>
        <div className="content">
          <div className="label">{props.label}</div>
          <div className="credit-badge">
            <BiCoin size={16} className="text-secondary-red" />
            {countText} {creditText} used
          </div>
        </div>
        <ProgressBar className={className} progress={percent} />
      </div>
    )
  }

  interface TwoNumberConsumptionCounterProps {
    title1: string
    color1?: string
    value1?: number
    title2: string
    color2?: string
    value2?: number
  }
  const TwoNumberConsumptionCounter = (
    props: TwoNumberConsumptionCounterProps
  ) => {
    function valueToString(value?: number) {
      return value !== undefined ? Util.absRounding(value) : '-'
    }
    return (
      <div className="two-number-consumption-counter">
        <div>
          <div>{props.title1}</div>
          <div
            className="counter"
            style={props.color1 ? { color: props.color1 } : undefined}
          >
            {valueToString(props.value1)}
          </div>
        </div>

        {props.value2 !== undefined && (
          <>
            <VerticalDivider height="48px" />

            <div>
              <div>{props.title2}</div>
              <div
                className="counter"
                style={props.color2 ? { color: props.color2 } : undefined}
              >
                {valueToString(props.value2)}
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className={fullClassName}>
      <div className="plan-details-container">
        <div className="plan-wrapper">
          <div className="plan-name-container">
            <div className="billing-component-title">{planName}</div>
            {(TierType.PayAsYouGo == props.invoice?.tier ||
              TierType.Subscription == props.invoice?.tier) && (
              <PriceContainer />
            )}
          </div>
          <PriceDescription />
        </div>

        {!AccountUtil.isTierEnterprise(props.invoice?.tier) &&
          <Button
            as={Link}
            to="/tierselection"
            label={
              TierType.Free == props.invoice?.tier
                ? 'Upgrade plan'
                : 'Change plan'
            }
            className="button"
            dark
          />}
      </div>

      {TierType.Free === props.invoice?.tier ? (
        <TwoNumberConsumptionCounter
          title1="Free credits left"
          value1={Util.absRounding(props.invoice?.baseCreditUsedCount)}
          title2="Paid credits left"
          value2={
            TierType.Free === props.invoice?.tier &&
            props.invoice?.paidCreditCount > 0
              ? Util.absRounding(props.invoice?.paidCreditCount)
              : undefined
          }
        />
      ) : TierType.TopUpCreditAimd === props.invoice?.tier ? (
        <TwoNumberConsumptionCounter
          title1="Credits left"
          value1={Util.absRounding(props.invoice?.baseCreditUsedCount)}
          title2=""
          value2={undefined}
        />
      ) : AccountUtil.isTierEnterprise(props.invoice?.tier) ? (
        // Credit consumption information for Enterprise tier.
        <TwoNumberConsumptionCounter
          title1="Credits used this month"
          value1={Util.absRounding(props.invoice?.periodCreditUsedCount)}
          title2="Total credits used"
          value2={Util.absRounding(props.invoice?.totalCreditUsedCount)}
          color2={'#F67D69'}
        />
      ) : (
        // Credit consumption information for other tiers.
        <>
          <CreditBar
            label="Credits"
            usedCount={props.invoice?.baseCreditUsedCount ?? 0}
            maxCount={props.invoice?.baseCreditMax ?? 0}
            className="plan-credit-bar"
            isMaxDisplayed={true}
          ></CreditBar>

          {TierType.Subscription == props.invoice?.tier && (
            <CreditBar
              label="Additional credits"
              usedCount={props.invoice?.offPackageCreditUsedCount ?? 0}
              maxCount={props.invoice?.offPackageCreditMax ?? 0}
              className="off-package-credit-bar"
            ></CreditBar>
          )}
        </>
      )}
    </div>
  )
}

export default PlanOverview
